import React from 'react';
import { TrendingUp, PieChart, Shield, BarChart3, Layers, Target, Zap, Clock } from 'lucide-react';

const WhatAreIndexes = () => {
  const benefits = [
    {
      icon: PieChart,
      title: 'Diversification',
      description: 'Spread risk across multiple assets instead of betting on a single stock or cryptocurrency.'
    },
    {
      icon: Shield,
      title: 'Lower Risk',
      description: 'Index funds reduce volatility by balancing gains and losses across the portfolio.'
    },
    {
      icon: TrendingUp,
      title: 'Market Performance',
      description: 'Track overall market growth rather than trying to pick individual winners.'
    },
    {
      icon: Clock,
      title: 'Passive Management',
      description: 'No need for constant monitoring - let the index do the work for you.'
    }
  ];

  return (
    <section className="py-24 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
              <span className="text-cyan-400 text-sm font-medium">Understanding Indexes</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              What Are Index Funds?
            </h2>
            <p className="text-gray-400 text-lg leading-relaxed mb-6">
              An index fund is a type of investment that tracks a specific market index, 
              such as the S&P 500 for stocks or a curated basket of cryptocurrencies. 
              Instead of picking individual assets, you invest in the entire market segment.
            </p>
            <p className="text-gray-400 leading-relaxed mb-8">
              At XPO, our index funds combine Forex pairs, CFDs, and cryptocurrencies into 
              expertly managed portfolios, giving you exposure to multiple markets through 
              a single investment.
            </p>
            
            {/* Visual Representation */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
              <h4 className="text-white font-semibold mb-4">Example: XPO Crypto Index</h4>
              <div className="space-y-3">
                {[
                  { name: 'Bitcoin (BTC)', percentage: 40, color: 'from-orange-500 to-orange-600' },
                  { name: 'Ethereum (ETH)', percentage: 30, color: 'from-blue-500 to-blue-600' },
                  { name: 'Other Altcoins', percentage: 30, color: 'from-cyan-500 to-cyan-600' }
                ].map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-300">{item.name}</span>
                      <span className="text-gray-400">{item.percentage}%</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-1000`}
                        style={{ width: `${item.percentage}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Benefits Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 border border-slate-700 rounded-2xl p-6 hover:border-cyan-500/50 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatAreIndexes;
